import { useEffect, useState } from 'react'
import { EyeOff, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { GestorFeedbackDocumentForm } from '@/components/Gestores/GestorFeedbackDocumentForm'
import { GestorFeedbackDocumentTable } from '@/components/Gestores/GestorFeedbackDocumentTable'
import type { GestorFeedbackDocumentFormValues } from '@/components/Gestores/gestorFeedbackConstants'
import type {
  GestorEquipeFeedback,
  GestorEquipeFeedbackTipo,
  GestorFeedbackDocumentPayload,
  GestorFeedbackGestorOption,
} from '@/services/gestoresService'
import { gestoresService } from '@/services/gestoresService'

type Props = {
  pjId: string
  profissionalNome: string
  feedbacks: GestorEquipeFeedback[]
  onCreate: (tipo: GestorEquipeFeedbackTipo, payload: GestorFeedbackDocumentPayload) => Promise<void>
  onUpdate: (feedbackId: string, payload: GestorFeedbackDocumentPayload) => Promise<void>
  onLiberar: (feedbackId: string) => Promise<void>
  onDelete: (feedbackId: string) => Promise<void>
}

function buildDocumentPayload(values: GestorFeedbackDocumentFormValues): GestorFeedbackDocumentPayload {
  return {
    tipo: 'FEEDBACK_PJ' as const,
    authorId: values.authorId,
    dataFeedback: values.dataFeedback,
    areaAtuacao: values.areaAtuacao,
    situacao: values.situacao,
    evidenciaUrl: values.evidenciaUrl,
    evidenciaFileName: values.evidenciaFileName,
    evidenciaMimeType: values.evidenciaMimeType,
    evidenciaLink: values.evidenciaLink.trim() || undefined,
    reportado: values.reportado,
    dataReport: values.reportado && values.dataReport ? values.dataReport : null,
    observacoesReporte: values.observacoesReporte.trim() || undefined,
  }
}

function AnotacoesPrivadasSection({
  items,
  onCreate,
  onDelete,
}: {
  items: GestorEquipeFeedback[]
  onCreate: Props['onCreate']
  onDelete: Props['onDelete']
}) {
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const handleCreate = async () => {
    if (!draft.trim()) return
    setSaving(true)
    try {
      await onCreate('ANOTACAO_PRIVADA', { conteudo: draft.trim() })
      setDraft('')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className='space-y-4'>
      <div>
        <h3 className='flex items-center gap-2 text-base font-semibold text-text-primary'>
          <EyeOff size={16} className='text-amber-500' />
          Anotações privadas
        </h3>
        <p className='mt-1 text-xs text-text-muted'>Somente gestores veem este conteúdo.</p>
      </div>
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        rows={3}
        placeholder='Anotação visível só para você…'
        className='w-full rounded-lg border border-border bg-bg-main px-3 py-2 text-sm text-text-primary'
      />
      <Button type='button' size='sm' disabled={saving || !draft.trim()} onClick={() => void handleCreate()}>
        Salvar anotação
      </Button>
      {items.length > 0 && (
        <ul className='space-y-2'>
          {items.map((item) => (
            <li key={item.id} className='rounded-lg border border-border px-3 py-3 text-sm'>
              <p className='whitespace-pre-wrap text-text-primary'>{item.conteudo}</p>
              <div className='mt-2 flex justify-between gap-2 text-xs text-text-muted'>
                <span>{item.author.name}</span>
                <Button type='button' size='sm' variant='destructive' onClick={() => void onDelete(item.id)}>
                  Excluir
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function GestorEquipeFeedbackPanel({
  pjId,
  profissionalNome,
  feedbacks,
  onCreate,
  onUpdate,
  onLiberar,
  onDelete,
}: Props) {
  const [gestores, setGestores] = useState<GestorFeedbackGestorOption[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<GestorEquipeFeedback | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void gestoresService.listFeedbackGestores().then(setGestores).catch(() => setGestores([]))
  }, [])

  const documentados = feedbacks.filter((item) => item.tipo === 'FEEDBACK_PJ')
  const anotacoes = feedbacks.filter((item) => item.tipo === 'ANOTACAO_PRIVADA')

  const handleSubmit = async (values: GestorFeedbackDocumentFormValues) => {
    setSaving(true)
    try {
      const payload = buildDocumentPayload(values)
      if (editing) {
        await onUpdate(editing.id, payload)
        setEditing(null)
      } else {
        await onCreate('FEEDBACK_PJ', payload)
        setShowForm(false)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='space-y-8'>
      <section className='space-y-4'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h3 className='flex items-center gap-2 text-base font-semibold text-text-primary'>
              <MessageSquare size={16} className='text-emerald-500' />
              Registro de feedback
            </h3>
            <p className='mt-1 text-xs text-text-muted'>
              Documentação no formato da planilha — com evidências, área de atuação e status de reporte.
            </p>
          </div>
          {!showForm && !editing && (
            <Button type='button' size='sm' onClick={() => setShowForm(true)}>
              Novo registro
            </Button>
          )}
        </div>

        {(showForm || editing) && (
          <GestorFeedbackDocumentForm
            pjId={pjId}
            profissionalNome={profissionalNome}
            gestores={gestores}
            initial={editing}
            saving={saving}
            onSubmit={handleSubmit}
            onUploadEvidence={(file) => gestoresService.uploadFeedbackEvidence(pjId, file)}
            onCancel={() => {
              setShowForm(false)
              setEditing(null)
            }}
          />
        )}

        <GestorFeedbackDocumentTable
          items={documentados}
          onEdit={(item) => {
            setShowForm(false)
            setEditing(item)
          }}
          onLiberar={onLiberar}
          onDelete={onDelete}
        />
      </section>

      <div className='border-t border-border pt-8'>
        <AnotacoesPrivadasSection items={anotacoes} onCreate={onCreate} onDelete={onDelete} />
      </div>
    </div>
  )
}
